import { type ReactElement, type ReactNode, useId } from 'react'

import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Rótulo + controle + dica + erro, amarrados pelos atributos de acessibilidade
 * corretos.
 *
 * O controle é passado por RENDER PROP em vez de `cloneElement`: assim o
 * componente entrega `id`, `aria-invalid` e `aria-describedby` já calculados, e
 * quem usa continua livre para colocar ali um `<Input>`, um `<Select>`, um
 * `<Textarea>` ou o que for — sem que este arquivo precise conhecer cada um.
 *
 * Erro sem `aria-describedby` é erro que o leitor de tela não lê. Como o painel
 * é usado por operação o dia inteiro, isso não é detalhe.
 */

export interface PropsDoControle {
  readonly id: string
  readonly 'aria-invalid': boolean | undefined
  readonly 'aria-describedby': string | undefined
  readonly 'aria-required': boolean | undefined
}

export interface CampoProps {
  readonly rotulo: string
  readonly erro?: string | undefined
  /** Texto de apoio permanente (formato esperado, consequência da escolha). */
  readonly dica?: string | undefined
  readonly obrigatorio?: boolean
  readonly className?: string
  readonly children: (controle: PropsDoControle) => ReactNode
}

export function Campo({
  rotulo,
  erro,
  dica,
  obrigatorio = false,
  className,
  children,
}: CampoProps): ReactElement {
  const id = useId()
  const idDaDica = `${id}-dica`
  const idDoErro = `${id}-erro`

  const descricoes = [dica === undefined ? null : idDaDica, erro === undefined ? null : idDoErro]
    .filter((valor): valor is string => valor !== null)
    .join(' ')

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id}>
        {rotulo}
        {obrigatorio ? (
          <span aria-hidden="true" className="ml-1 text-fg-muted">
            *
          </span>
        ) : null}
      </Label>

      {children({
        id,
        'aria-invalid': erro === undefined ? undefined : true,
        'aria-describedby': descricoes.length > 0 ? descricoes : undefined,
        'aria-required': obrigatorio ? true : undefined,
      })}

      {dica === undefined ? null : (
        <p id={idDaDica} className="text-xs text-fg-muted">
          {dica}
        </p>
      )}

      {erro === undefined ? null : (
        <p id={idDoErro} className="text-xs text-danger-fg">
          {erro}
        </p>
      )}
    </div>
  )
}
